import ReportsAPI from 'dashboard/api/reports';
import {
  formatUnixUtcDateTime,
  unixRangeForUtcDateLabel,
} from './utcDateHelper';

export const SUMMARY_REPORT_ARCHIVE_STORAGE_KEY =
  'summaryReportArchiveNavigation';

const LINKABLE_COUNT_METRICS = new Set([
  'conversationsCount',
  'uniqueCyclesHandled',
  'assignmentEpisodesCount',
  'answeredEpisodesCount',
  'unansweredEpisodesCount',
  'transfersInCount',
  'transfersOutCount',
  'resolutionsCount',
  'backlogEnd',
  'reopenedCount',
  'botResolvedCount',
  'humanResolvedCount',
  'unresolvedCount',
  'firstResponseCount',
  'replyTimeCount',
  'csatResponses',
  'surveysSent',
  'csatSatisfaction',
  'csatNegative',
  'botStartedCount',
  'botHandoffCount',
  'botFallbackCount',
  'botRepeatRequestCount',
  'queueEntriesCount',
  'queueUniqueCount',
  'queueAcceptedCount',
  'queueAbandonedCount',
  'queueWaitingCount',
  'queuePeakCount',
]);

const RESOLVED_METRICS = new Set([
  'resolutionsCount',
  'botResolvedCount',
  'humanResolvedCount',
]);
const REOPENED_METRICS = new Set(['reopenedCount']);
const HISTORICAL_ARCHIVE_METRICS = new Set([
  'conversationsCount',
  'assignmentEpisodesCount',
  'answeredEpisodesCount',
  'unansweredEpisodesCount',
  'transfersOutCount',
  'transfersInCount',
  'backlogEnd',
  'resolutionsCount',
  'botResolvedCount',
  'humanResolvedCount',
  'reopenedCount',
  'csatResponses',
  'surveysSent',
  'csatSatisfaction',
  'csatNegative',
  'queuePeakCount',
  'queueAcceptedCount',
  'queueEntriesCount',
  'queueUniqueCount',
  'queueAbandonedCount',
  'queueWaitingCount',
  'firstResponseCount',
  'replyTimeCount',
  'unresolvedCount',
]);

const EPISODE_ID_METRICS = new Set([
  'assignmentEpisodesCount',
  'answeredEpisodesCount',
  'unansweredEpisodesCount',
  'transfersInCount',
  'transfersOutCount',
]);

const METRIC_EPISODE_RESULT = {
  answeredEpisodesCount: 'answered',
  unansweredEpisodesCount: 'unanswered',
  transfersOutCount: 'transferred',
  transfersInCount: 'transferred',
  resolutionsCount: 'completed',
};

const hasEpisodeSnapshotFilter = filters =>
  filters.some(filter =>
    ['cw_episode_result', 'cw_receipt_type'].includes(filter.attribute_key)
  );

export const buildFilterClause = (
  attributeKey,
  filterOperator,
  values,
  queryOperator = 'and'
) => ({
  attribute_key: attributeKey,
  filter_operator: filterOperator,
  values,
  query_operator: queryOperator,
});

const buildStatusFilter = statuses => {
  const list = Array.isArray(statuses) ? statuses : [statuses];
  return buildFilterClause(
    'status',
    'equal_to',
    list.map(status => ({ id: status, name: status }))
  );
};

const buildTextFilter = (attributeKey, value) =>
  buildFilterClause(attributeKey, 'equal_to', [value]);

const appendEpisodeSnapshotFilters = (filters, context, metricKey) => {
  if (EPISODE_ID_METRICS.has(metricKey)) return filters;

  const { entityFilters } = context;
  if (!entityFilters) return filters;

  const episodeResult =
    entityFilters.episodeResult && entityFilters.episodeResult !== 'all'
      ? entityFilters.episodeResult
      : METRIC_EPISODE_RESULT[metricKey];

  if (episodeResult && !hasEpisodeSnapshotFilter(filters)) {
    filters.push(buildTextFilter('cw_episode_result', episodeResult));
  }

  if (
    entityFilters.receiptType &&
    entityFilters.receiptType !== 'all' &&
    !filters.some(filter => filter.attribute_key === 'cw_receipt_type')
  ) {
    filters.push(buildTextFilter('cw_receipt_type', entityFilters.receiptType));
  }

  return filters;
};

const hasStatusFilter = filters =>
  filters.some(filter => filter.attribute_key === 'status');

const appendMetricHintFilters = (filters, metricKey) => {
  if (hasEpisodeSnapshotFilter(filters)) return filters;

  // Status for backlog/resolutions is applied in buildArchiveFilters — avoid duplicates.
  if (metricKey === 'backlogEnd' || metricKey === 'resolutionsCount') {
    return filters;
  }

  if (metricKey === 'reopenedCount') {
    filters.push(buildTextFilter('cw_receipt_type', 'reopen'));
  } else if (metricKey === 'botResolvedCount') {
    if (!hasStatusFilter(filters)) filters.push(buildStatusFilter('resolved'));
    filters.push(buildTextFilter('cw_episode_result', 'bot'));
  } else if (metricKey === 'humanResolvedCount') {
    if (!hasStatusFilter(filters)) filters.push(buildStatusFilter('resolved'));
    filters.push(buildTextFilter('cw_episode_result', 'human'));
  } else if (metricKey === 'unresolvedCount') {
    if (!hasStatusFilter(filters)) {
      filters.push(buildStatusFilter(['open', 'pending', 'snoozed']));
    }
  } else if (metricKey === 'csatResponses') {
    filters.push(buildFilterClause('csat_response_rating', 'is_present', []));
  } else if (metricKey === 'botHandoffCount') {
    filters.push(
      buildStatusFilter(['open', 'pending', 'queued', 'snoozed', 'resolved'])
    );
  } else if (metricKey === 'botFallbackCount') {
    filters.push(buildTextFilter('cw_episode_result', 'fallback'));
  } else if (metricKey === 'queueAcceptedCount') {
    filters.push(buildStatusFilter(['open', 'resolved', 'pending', 'snoozed']));
  } else if (metricKey === 'queueAbandonedCount') {
    filters.push(buildStatusFilter('resolved'));
  } else if (metricKey === 'queueWaitingCount') {
    filters.push(buildStatusFilter('queued'));
  }

  return filters;
};

export const CARD_METRIC_MAP = {
  agent: {
    accepted: 'assignmentEpisodesCount',
    answered: 'answeredEpisodesCount',
    unanswered: 'unansweredEpisodesCount',
    resolved: 'resolutionsCount',
    transferred: 'transfersOutCount',
    'median-frt': 'answeredEpisodesCount',
    'avg-art': 'replyTimeCount',
    'median-assignment': 'assignmentEpisodesCount',
  },
  team: {
    episodes: 'assignmentEpisodesCount',
    resolved: 'resolutionsCount',
    'transfers-in': 'transfersInCount',
    'transfers-out': 'transfersOutCount',
    transfers: 'transfersOutCount',
    backlog: 'backlogEnd',
    'median-frt': 'firstResponseCount',
    'median-handling': 'assignmentEpisodesCount',
  },
  label: {
    tagged: 'conversationsCount',
    'without-tags': 'conversationsCount',
    resolved: 'resolutionsCount',
    conversations: 'conversationsCount',
    backlog: 'backlogEnd',
    'median-frt': 'firstResponseCount',
  },
  inbox: {
    created: 'conversationsCount',
    resolved: 'resolutionsCount',
    backlog: 'backlogEnd',
    largest: 'conversationsCount',
  },
  account: {
    created: 'conversationsCount',
    resolved: 'resolutionsCount',
    backlog: 'backlogEnd',
    reopened: 'reopenedCount',
    'bot-resolved': 'botResolvedCount',
    'median-frt': 'firstResponseCount',
    'median-resolution': 'resolutionsCount',
    csat: 'csatResponses',
  },
  csat: {
    'surveys-sent': 'surveysSent',
    'total-responses': 'csatResponses',
    'response-rate': 'csatResponses',
    'average-rating': 'csatResponses',
    'satisfaction-score': 'csatSatisfaction',
    'negative-rate': 'csatNegative',
  },
  bot: {
    started: 'botStartedCount',
    resolved: 'botResolvedCount',
    resolution_rate: 'botResolvedCount',
    handed_off: 'botHandoffCount',
    handoff_rate: 'botHandoffCount',
    median_handoff: 'botHandoffCount',
    repeat: 'botRepeatRequestCount',
    fallback: 'botFallbackCount',
  },
  queue: {
    entries: 'queueEntriesCount',
    unique: 'queueUniqueCount',
    accepted: 'queueAcceptedCount',
    abandoned: 'queueAbandonedCount',
    waiting: 'queueWaitingCount',
    median: 'queueAcceptedCount',
    peak: 'queuePeakCount',
  },
};

export const TIME_CELL_METRIC_MAP = {
  avgFirstResponseTime: 'firstResponseCount',
  medianFirstResponseTime: 'firstResponseCount',
  avgOnlineFirstResponseTime: 'answeredEpisodesCount',
  medianOnlineFirstResponseTime: 'answeredEpisodesCount',
  avgResolutionTime: 'resolutionsCount',
  medianResolutionTime: 'resolutionsCount',
  medianFullResolutionSeconds: 'resolutionsCount',
  medianTeamHandlingSeconds: 'assignmentEpisodesCount',
  medianAssignmentDuration: 'assignmentEpisodesCount',
  avgReplyTime: 'replyTimeCount',
  csat: 'csatResponses',
};

export const resolveMetricKeyFromCardKey = (reportType, cardKey) =>
  CARD_METRIC_MAP[reportType]?.[cardKey] || null;

export const isMetricLinkable = (metricKey, reportType) => {
  if (!metricKey || !reportType) return false;
  return LINKABLE_COUNT_METRICS.has(metricKey);
};

const formatArchiveDateTime = unixSeconds => formatUnixUtcDateTime(unixSeconds);

const buildDateRangeFilters = (
  fromUnix,
  toUnix,
  attributeKey = 'created_at'
) => {
  if (!fromUnix || !toUnix) return [];

  return [
    buildFilterClause(attributeKey, 'is_from', [
      formatArchiveDateTime(fromUnix),
    ]),
    buildFilterClause(attributeKey, 'is_to', [formatArchiveDateTime(toUnix)]),
  ];
};

const buildIdFilter = (attributeKey, id) => {
  if (!id) return null;
  return buildFilterClause(attributeKey, 'equal_to', [Number(id)]);
};

const buildIdListFilter = (attributeKey, ids) => {
  if (!ids?.length) return null;
  if (ids.length === 1) return buildIdFilter(attributeKey, ids[0]);
  return buildFilterClause(
    attributeKey,
    'equal_to',
    ids.map(id => Number(id))
  );
};

const buildLabelsFilter = labelTitle => {
  if (!labelTitle) return null;
  return buildFilterClause('labels', 'equal_to', [labelTitle]);
};

const appendScopeFilters = (filters, context) => {
  const { entityFilters, reportType } = context;
  if (!entityFilters) return filters;

  const append = clause => {
    if (clause) filters.push(clause);
  };

  if (reportType !== 'agent') {
    append(buildIdListFilter('assignee_id', entityFilters.agentIds));
  }
  if (reportType !== 'team') {
    append(buildIdListFilter('team_id', entityFilters.teamIds));
  }
  if (reportType !== 'inbox') {
    append(buildIdListFilter('inbox_id', entityFilters.inboxIds));
  }
  if (reportType !== 'label' && entityFilters.labelIds?.length) {
    append(
      buildFilterClause('labels', 'equal_to', entityFilters.labelTitles || [])
    );
  }

  if (entityFilters.excludeLabelIds?.length) {
    append(
      buildFilterClause(
        'labels',
        'not_equal_to',
        entityFilters.excludeLabelTitles || []
      )
    );
  }

  if (entityFilters.tagPresence === 'without') {
    append(buildFilterClause('labels', 'is_not_present', []));
  }

  // Chats filters → closest archive snapshots (cw_* additional_attributes / status).
  if (
    entityFilters.statusAtPeriodEnd &&
    entityFilters.statusAtPeriodEnd !== 'all' &&
    !filters.some(filter => filter.attribute_key === 'status')
  ) {
    append(buildStatusFilter(entityFilters.statusAtPeriodEnd));
  }

  if (
    entityFilters.reopening === 'only_reopened' &&
    !filters.some(filter => filter.attribute_key === 'cw_receipt_type')
  ) {
    append(buildTextFilter('cw_receipt_type', 'reopen'));
  }

  if (
    ['with_agent', 'with_team'].includes(entityFilters.transfers) &&
    !hasEpisodeSnapshotFilter(filters)
  ) {
    append(buildTextFilter('cw_episode_result', 'transferred'));
  }

  if (
    entityFilters.humanResponse === 'absent' &&
    !hasEpisodeSnapshotFilter(filters)
  ) {
    append(buildTextFilter('cw_episode_result', 'unanswered'));
  } else if (
    entityFilters.humanResponse === 'present' &&
    !hasEpisodeSnapshotFilter(filters)
  ) {
    append(buildTextFilter('cw_episode_result', 'answered'));
  }

  return filters;
};

const appendEntityFilter = (filters, reportType, row, context, metricKey) => {
  if (!row?.id && row?.id !== 0) return filters;

  if (Number(row.id) === 0) {
    filters.push(buildFilterClause('labels', 'is_not_present', []));
    return filters;
  }

  switch (reportType) {
    case 'agent':
      appendScopeFilters(filters, context);
      filters.push(buildIdFilter('assignee_id', row.id));
      break;
    case 'team':
      appendScopeFilters(filters, context);
      filters.push(buildIdFilter('team_id', row.id));
      break;
    case 'inbox':
      appendScopeFilters(filters, context);
      filters.push(buildIdFilter('inbox_id', row.id));
      break;
    case 'label':
      appendScopeFilters(filters, context);
      if (row.name && Number(row.id) !== 0) {
        if (metricKey === 'conversationsCount') {
          filters.push(buildLabelsFilter(row.name));
        } else if (!RESOLVED_METRICS.has(metricKey)) {
          filters.push(buildLabelsFilter(row.name));
        }
      }
      break;
    default:
      appendScopeFilters(filters, context);
  }

  return filters;
};

export const buildArchiveFilters = ({
  metricKey,
  reportType,
  row,
  context,
}) => {
  const filters = [];
  const isBacklog = metricKey === 'backlogEnd';
  const isResolved = RESOLVED_METRICS.has(metricKey);
  const isReopened = REOPENED_METRICS.has(metricKey);

  // TZ §5.4: date attribute depends on metric (created vs resolve/reopen proxy).
  if (isBacklog) {
    const asOf = context.to;
    if (asOf) {
      filters.push(
        buildFilterClause('created_at', 'is_to', [formatArchiveDateTime(asOf)])
      );
    }
    filters.push(buildStatusFilter(['open', 'pending', 'snoozed']));
  } else if (isResolved || isReopened) {
    // No resolved_at filter key — last_activity_at is the closest conversation field.
    filters.push(
      ...buildDateRangeFilters(context.from, context.to, 'last_activity_at')
    );
    if (isResolved) {
      filters.push(buildStatusFilter('resolved'));
    }
  } else {
    filters.push(...buildDateRangeFilters(context.from, context.to));
  }

  appendEntityFilter(filters, reportType, row, context, metricKey);

  if (
    reportType === 'label' &&
    RESOLVED_METRICS.has(metricKey) &&
    row?.name &&
    Number(row.id) !== 0
  ) {
    filters.push(
      buildFilterClause('cw_resolve_labels', 'contains', [row.name])
    );
  }

  if (!row?.id && row?.id !== 0) {
    appendScopeFilters(filters, context);
  }

  if (context.entityFilters?.tagPresence === 'without' && !row?.id) {
    filters.push(buildFilterClause('labels', 'is_not_present', []));
  }

  appendEpisodeSnapshotFilters(filters, context, metricKey);
  appendMetricHintFilters(filters, metricKey);

  return filters;
};

export const buildArchiveRoute = ({ accountId, reportType, row }) => {
  const params = { accountId: String(accountId) };

  if (row?.id && Number(row.id) !== 0) {
    switch (reportType) {
      case 'team':
        return {
          name: 'team_conversations',
          params: { ...params, teamId: row.id },
        };
      case 'inbox':
        return {
          name: 'inbox_dashboard',
          params: { ...params, inbox_id: row.id },
        };
      case 'label':
        return { name: 'home', params };
      default:
        return { name: 'home', params };
    }
  }

  if (Number(row?.id) === 0) {
    return { name: 'home', params };
  }

  return { name: 'home', params };
};

export const buildTransferMatrixArchiveNavigation = ({
  accountId,
  fromTeam,
  toTeam,
  context,
}) => {
  if (fromTeam?.id === toTeam?.id) return null;
  if (fromTeam?.id == null && toTeam?.id == null) return null;

  const filters = [];
  filters.push(...buildDateRangeFilters(context.from, context.to));
  if (toTeam?.id) {
    filters.push(buildIdFilter('team_id', toTeam.id));
  }

  appendScopeFilters(filters, {
    ...context,
    reportType: 'team',
    entityFilters: {
      ...context.entityFilters,
      teamIds: (context.entityFilters?.teamIds || []).filter(
        id => Number(id) !== Number(toTeam.id)
      ),
    },
  });
  appendEpisodeSnapshotFilters(filters, context, 'transfersOutCount');

  return {
    route: buildArchiveRoute({
      accountId,
      reportType: 'team',
      row: fromTeam,
    }),
    filters,
    chatStatus: 'all',
  };
};

const unixRangeFromArchiveFilters = filters => {
  if (!filters?.length) return null;

  const dateKeys = new Set(['created_at', 'last_activity_at']);
  const parseValue = value => {
    const raw = Array.isArray(value) ? value[0] : value;
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    const text = String(raw);
    const stamp = /Z$|[+-]\d{2}:\d{2}$/.test(text) ? text : `${text}Z`;
    const ms = Date.parse(stamp);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  };
  const fromClause = filters.find(
    filter =>
      dateKeys.has(filter.attribute_key) && filter.filter_operator === 'is_from'
  );
  const toClause = filters.find(
    filter =>
      dateKeys.has(filter.attribute_key) && filter.filter_operator === 'is_to'
  );
  if (!fromClause && !toClause) return null;

  return {
    from: parseValue(fromClause?.values),
    to: parseValue(toClause?.values),
  };
};

const persistAndNavigate = (
  router,
  { accountId, reportType, row, filters }
) => {
  sessionStorage.setItem(
    SUMMARY_REPORT_ARCHIVE_STORAGE_KEY,
    JSON.stringify({ filters, chatStatus: 'all' })
  );
  router.push(buildArchiveRoute({ accountId, reportType, row }));
};

const navigateWithArchiveToken = async (
  router,
  { accountId, reportType, row, intent }
) => {
  try {
    const { data } = await ReportsAPI.createArchiveIntent(intent);
    const token = data?.archive_token;
    if (!token) throw new Error('missing_token');

    const route = buildArchiveRoute({ accountId, reportType, row });
    router.push({
      ...route,
      query: { ...(route.query || {}), archive_token: token },
    });
    return true;
  } catch {
    // TZ §5.4/§19.4: never fall back to current-status filters for historical metrics.
    if (
      HISTORICAL_ARCHIVE_METRICS.has(intent.metric_key) ||
      intent.report_type === 'csat'
    ) {
      return false;
    }
    const filters = buildArchiveFilters({
      metricKey: intent.metric_key,
      reportType,
      row,
      context: {
        from: intent.from,
        to: intent.to,
        entityFilters: intent.entity_filters,
        reportType,
      },
    });
    if (filters.length) {
      persistAndNavigate(router, { accountId, reportType, row, filters });
      return true;
    }
    return false;
  }
};

export const navigateToSummaryArchive = async (
  router,
  {
    accountId,
    metricKey,
    reportType,
    row,
    context,
    filters: presetFilters,
    ratings,
    csatResponded,
    durationMin,
    durationMax,
    durationEvent,
  }
) => {
  if (
    presetFilters?.length &&
    !HISTORICAL_ARCHIVE_METRICS.has(metricKey) &&
    reportType !== 'csat'
  ) {
    persistAndNavigate(router, {
      accountId,
      reportType,
      row,
      filters: presetFilters,
    });
    return true;
  }

  if (reportType !== 'csat' && !isMetricLinkable(metricKey, reportType)) {
    return false;
  }
  if (reportType === 'csat' && !metricKey) {
    return false;
  }

  const intent = {
    metric_key: metricKey,
    report_type: reportType,
    from: context?.from,
    to: context?.to,
    row: row ? { id: row.id, name: row.name } : undefined,
    entity_filters: context?.entityFilters || {},
    ratings,
    csat_responded: csatResponded,
    duration_min: durationMin,
    duration_max: durationMax,
    duration_event: durationEvent,
  };

  if (HISTORICAL_ARCHIVE_METRICS.has(metricKey) || reportType === 'csat') {
    const bucketRange = unixRangeFromArchiveFilters(presetFilters);
    if (bucketRange?.from) intent.from = bucketRange.from;
    if (bucketRange?.to) intent.to = bucketRange.to;
    return navigateWithArchiveToken(router, {
      accountId,
      reportType,
      row,
      intent,
    });
  }

  const filters = buildArchiveFilters({
    metricKey,
    reportType,
    row,
    context,
  });
  if (!filters.length) return false;

  persistAndNavigate(router, { accountId, reportType, row, filters });
  return true;
};

const applyArchivePayload = ({
  store,
  fetchFilteredConversations,
  appliedFilterRef,
  activeStatusRef,
  useCamelCase,
  filters,
  chatStatus = 'all',
}) => {
  if (!filters?.length) return false;

  appliedFilterRef.value = useCamelCase(JSON.parse(JSON.stringify(filters)));
  store.dispatch('setConversationFilters', filters);

  if (chatStatus) {
    activeStatusRef.value = chatStatus;
    store.dispatch('setChatStatusFilter', chatStatus);
  }

  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(useCamelCase(JSON.parse(JSON.stringify(filters))));
  return true;
};

export const applyPendingSummaryReportArchive = async ({
  store,
  fetchFilteredConversations,
  appliedFilterRef,
  activeStatusRef,
  useCamelCase,
  archiveToken,
}) => {
  if (archiveToken) {
    try {
      const { data } = await ReportsAPI.resolveArchiveIntent(archiveToken);
      return applyArchivePayload({
        store,
        fetchFilteredConversations,
        appliedFilterRef,
        activeStatusRef,
        useCamelCase,
        filters: data?.filters,
        chatStatus: data?.chat_status || 'all',
      });
    } catch {
      return false;
    }
  }

  const raw = sessionStorage.getItem(SUMMARY_REPORT_ARCHIVE_STORAGE_KEY);
  if (!raw) return false;

  sessionStorage.removeItem(SUMMARY_REPORT_ARCHIVE_STORAGE_KEY);

  let pending;
  try {
    pending = JSON.parse(raw);
  } catch {
    return false;
  }

  return applyArchivePayload({
    store,
    fetchFilteredConversations,
    appliedFilterRef,
    activeStatusRef,
    useCamelCase,
    filters: pending.filters,
    chatStatus: pending.chatStatus,
  });
};

export const resolveResolutionBreakdownMetricKey = datasetIndex =>
  ['botResolvedCount', 'humanResolvedCount', 'unresolvedCount'][datasetIndex];

export const buildHourArchiveFilters = ({
  metricKey,
  reportType,
  row,
  context,
  dateKey,
  hour,
}) => {
  const range = unixRangeForUtcDateLabel(
    `${String(dateKey).slice(0, 10)} ${String(hour).padStart(2, '0')}`,
    'hour'
  );
  if (!range) {
    return buildArchiveFilters({ metricKey, reportType, row, context });
  }

  return buildArchiveFilters({
    metricKey,
    reportType,
    row,
    context: {
      ...context,
      from: range.from,
      to: range.to,
    },
  });
};

export const buildWeekdayHourArchiveFilters = ({
  metricKey,
  reportType,
  row,
  context,
  weekday,
  hour,
}) => {
  const fromUnix = Number(context?.from) || 0;
  const toUnix = Number(context?.to) || 0;
  if (!fromUnix || !toUnix) {
    return buildArchiveFilters({ metricKey, reportType, row, context });
  }

  const matchingStarts = [];
  let cursor = fromUnix;
  while (cursor <= toUnix) {
    const date = new Date(cursor * 1000);
    if (date.getUTCDay() === weekday && date.getUTCHours() === hour) {
      matchingStarts.push(
        Math.floor(
          Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            hour,
            0,
            0
          ) / 1000
        )
      );
    }
    cursor += 3600;
  }

  // Single matching slot → exact hour window. Multiple weeks → keep full period
  // (conversation filters cannot OR disjoint hour windows).
  if (matchingStarts.length === 1) {
    const slotFrom = matchingStarts[0];
    return buildArchiveFilters({
      metricKey,
      reportType,
      row,
      context: {
        ...context,
        from: slotFrom,
        to: Math.min(slotFrom + 3600 - 1, toUnix),
      },
    });
  }

  return buildArchiveFilters({ metricKey, reportType, row, context });
};

export const buildDailyArchiveFilters = ({
  metricKey,
  reportType,
  row,
  context,
  dateLabel,
}) => {
  const dayRange = unixRangeForUtcDateLabel(dateLabel, 'day');
  const dayContext = dayRange
    ? { ...context, from: dayRange.from, to: dayRange.to }
    : context;

  return buildArchiveFilters({
    metricKey,
    reportType,
    row,
    context: dayContext,
  });
};

export const buildBucketArchiveFilters = ({
  metricKey,
  reportType,
  row,
  context,
  dateLabel,
  granularity = 'day',
}) => {
  if (granularity === 'hour') {
    const match = String(dateLabel).match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2})/);
    if (match) {
      return buildHourArchiveFilters({
        metricKey,
        reportType,
        row,
        context,
        dateKey: match[1],
        hour: Number(match[2]),
      });
    }
  }

  const bucketRange = unixRangeForUtcDateLabel(dateLabel, granularity);
  if (!bucketRange) {
    return buildArchiveFilters({ metricKey, reportType, row, context });
  }

  return buildArchiveFilters({
    metricKey,
    reportType,
    row,
    context: {
      ...context,
      from: bucketRange.from,
      to: context?.to ? Math.min(bucketRange.to, context.to) : bucketRange.to,
    },
  });
};

export const buildConversationArchiveFilters = ({
  conversationDisplayId,
  reportType,
  row,
  context,
}) => {
  const filters = appendScopeFilters([], { ...context, reportType });
  if (row?.id) {
    appendEntityFilter(filters, reportType, row, context, null);
  }
  filters.push(buildIdFilter('display_id', conversationDisplayId));
  return filters;
};
